import React, { useEffect, useState } from 'react';
import {
	combineLatestWith, filter, Subject, takeUntil, tap,
} from 'rxjs';

import {
	Modal,
	Button,
	IconColorBit,
	IllustrationBits,
} from '@sololearnorg/sol';
import useStyles from 'isomorphic-style-loader/useStyles';
import {
	Container,
	BitService,
	ShopItemType,
	DataService,
	i18n,
} from '../../../../../symphony';

import { SlDataService } from '../../../services/sl-data.service';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import { SlLEContext } from '../../../global-constants';
import { ActionType, RequestType } from '../../../global-interface';

import s from './UnlockQuizAnswer.scss';
import { useDisableKeyPress } from '../../../utils/helpers';

export function UnlockQuizAnswer(): JSX.Element {
	useStyles(s);
	const [isAvailable, setIsAvailable] = useState<boolean>(false);
	const [quizAnswerPrice, setQuizAnswerPrice] = useState<number>(null);
	const [userBits, setUserBits] = useState<number>(null);

	const { actions$ } = Container.take(SlLEContext, SlActions);

	useDisableKeyPress();

	useEffect(() => {
		const unsubscriber = new Subject<void>();
		const { shopItemsList$ } = Container.take('global', DataService);
		const { bits$ } = Container.take('global', BitService);

		bits$
			.pipe(
				takeUntil(unsubscriber),
				combineLatestWith(shopItemsList$),
				filter(([b, si]) => !!b && !!si),
				tap(([bits, shopItems]) => {
					const { price } = shopItems.find(
						(si) => si.id === ShopItemType.QuizAnswer,
					);
					setUserBits(bits.bits);
					setQuizAnswerPrice(price);
					setIsAvailable(bits.bits >= price);
					actions$.next({
						type: ActionType.OnUnlockQuizAnswerMount,
						requestType: RequestType.Parallel,
					});
				}),
			)
			.subscribe();

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	const renderModalDescription = () => (isAvailable ? (
		<>
			<div>
				<IllustrationBits />
			</div>
			<p className="unlockQuizAnswer__headline">
				{i18n.t('web-bits.modal-title')}
			</p>
			<p sl-test-data="elAvailableBitsText">
				{i18n.t('web-bits.modal-description')}
			</p>
			<p>
				(
				{i18n.t('web-bits.you-have-label')}
				{' '}
				<IconColorBit className="le-bit-icon" />
				{' '}
				{userBits}
				)
			</p>
		</>
	) : (
		<>
			<p sl-test-data="elNotAvailableBitsText">
				{i18n.t(
					userBits > 0
						? 'web-bits.not-available-bits-warning'
						: 'web-bits.not-available-0-bits-warning',
				)}
				{' '}
				<IconColorBit className="le-bit-icon" />
				{' '}
				<b>
					{userBits}
					.
				</b>
				{' '}
				{i18n.t('web-bits.bits-earn-more-line1')}
			</p>
			<p>{i18n.t('web-bits.bits-earn-more-line2')}</p>
		</>
	));

	const closeModal = (): void => {
		const { openUnlockQuizAnswerModal$ } = Container.take(
			SlLEContext,
			SlDataService,
		);
		openUnlockQuizAnswerModal$.next(false);
	};

	const onClose = (): void => {
		actions$.next({
			type: ActionType.OnUnlockQuizAnswerCloseClick,
			requestType: RequestType.Parallel,
		});
		closeModal();
	};

	const onUnlockClick = (): void => {
		closeModal();
		actions$.next({
			type: ActionType.OnUnlockQuizAnswerButtonClick,
			requestType: RequestType.Cancelable,
		});
	};

	return (
		<Modal.Default
			dataTestid="mdlUnlockQuizAnswer"
			onClose={onClose}
			actions={[
				<Button
					buttonType="secondary"
					onClick={onUnlockClick}
					key={1}
					disabled={!isAvailable}
				>
					{i18n.t('web-bits.unlock-cta')}
					{' '}
					<IconColorBit className="le-bit-icon" />
					{' '}
					{quizAnswerPrice}
				</Button>,
			]}
			description={renderModalDescription()}
		/>
	);
}
